import type { PrivatePoolLimits, VenueConfig } from "@aluume/shared/lib/types/venue";

// Party-cap math for the booking pickers, driven entirely by the live per-pool
// unit budget now carried on VenueConfig (privateGuestUnits / privateChildWeight
// / privateMaxAdults / privateMaxChildren — populated from the `resources` row by
// loadVenueConfig, with the static venues.ts values as fallback). The Pool
// Settings page is therefore the single source of truth: editing a pool's max
// adults / children / adult spots flows straight through to the customer, staff
// and embed flows.
//
// PER-CATEGORY: bookings are made against a CATEGORY (pool type), so each helper
// takes an optional poolSlug. When given, it reads that category's pools from
// config.privatePoolLimits (the caps of EVERY active pool in the category — see
// loadVenueConfig) and returns the MOST GENEROUS pool's cap, so a large party
// stays selectable up to the biggest pool while a category whose biggest pool is
// smaller caps the picker correctly. The availability route then hides the times
// where no pool that actually fits the chosen party is free. Without a slug the
// helpers fall back to the venue-wide aggregate.
//
// The model reproduces the previous hard-coded Bathe rule EXACTLY (4 adult spots,
// child = 0.65 of a spot → 4 adults, or 2 adults + 3 children) and matches what
// create_hold enforces server-side: adults + children·child_weight ≤ guest_units.
// (max_adults / max_children are picker-only upper caps — create_hold enforces
// only the unit budget.) Pure functions — safe to import from both client
// components and the server.

const EPS = 1e-9; // mirror PoolsTab.fitsCaption — guard floor() against float drift

/** Does a party fit ONE private pool's unit budget? Mirrors the server rule EXACTLY
 *  (`create_hold`'s `AND NOT p_override_capacity` branch and `resolveChangeSlot`,
 *  `change-slot.ts`): `adults + max(0,children)·child_weight ≤ guest_units`. A null /
 *  undefined budget is unconstrained (returns true). Pure — safe on client and server.
 *
 *  Use this for the staff sheet's over-capacity decision instead of re-deriving the
 *  ceiling with `floor(guest_units − children·weight)`: the floored form drifts from the
 *  server on a sub-unit budget (`max(1, …)` clamp) and, more importantly, the picker's
 *  category-max fallback reads the MOST GENEROUS pool in a category — so on the smaller
 *  pool of a mixed category (Sanctuary Pool 2, budget 2) the sheet thinks the party fits,
 *  never sends `overrideCapacity`, and the server rejects it (AP-6005 / AP-6247). Given
 *  the booked pool's real budget, this returns the same verdict the server will. */
export function fitsPoolBudget(
  adults: number,
  children: number,
  guestUnits: number | null | undefined,
  childWeight: number | null | undefined = 0.65,
): boolean {
  if (guestUnits == null) return true; // no unit budget on this pool → unconstrained
  const weight = Number(childWeight ?? 0.65);
  return adults + Math.max(0, children) * weight <= guestUnits + EPS;
}

/** Resolve the unit budget the staff change sheet should judge a party against, as a PAIR.
 *
 *  `guest_units` and `child_weight` are one pool's calibration and must travel together: mixing a
 *  budget of 2 (Pool 2) with a weight of 0.65 (its Sanctuary sibling Pool 1 uses 0.65, Pool 2 uses
 *  0.50) yields a verdict for a pool that does not exist, and the server — which reads both from
 *  the same row — then disagrees. So a source wins or loses as a whole, never field by field.
 *
 *  Precedence is deliberate. The save POSTs `selectedSlot.resource_id`, and the server budgets THAT
 *  resource, so a selected slot is authoritative outright — including a null budget, which means
 *  the pool is genuinely unconstrained rather than "ask somewhere else".
 *
 *  The second argument is a SEED for the state before any slot is chosen, not "the booked pool":
 *  its two producers disagree on meaning. BookingDetailSheet passes the booking's own pool, while
 *  ScheduleBoard's drag passes the DROP-TARGET pool. Both are only ever a starting display, which
 *  the first real slot replaces — so the ambiguity is contained here rather than acted on.
 *
 *  `fitsPoolBudget` was never wrong; the budget simply never REACHED it — see AP-6005, AP-6247 and
 *  AP-8067, where the sheet fell back to the category's most generous pool, decided the party
 *  fitted, never sent `overrideCapacity`, and the server refused with `party_too_large`.
 *
 *  A null result means genuinely unconstrained (no unit budget on this pool), which is exactly how
 *  `fitsPoolBudget` reads it. */
/** A budget figure that is safe to compare against. `Number()` alone is fail-DANGEROUS here:
 *  `NaN != null` is true, so a junk value would sail through every `!= null` gate and then make
 *  `fitsPoolBudget` return false for EVERY party — sending `overrideCapacity` on every save, which
 *  makes `resolveChangeSlot` skip `party_too_large` outright. A bad number must degrade to "no
 *  budget" (unconstrained), never to "a budget nothing can satisfy". */
const budgetUnits = (v: unknown): number | null => {
  const n = Number(v);
  return v != null && Number.isFinite(n) && n > 0 ? n : null;
};
const budgetWeight = (v: unknown): number | null => {
  const n = Number(v);
  // 0 is legitimate (a pool where children don't consume units), so this floor differs from units.
  return v != null && Number.isFinite(n) && n >= 0 ? n : null;
};

export function resolvePoolBudget(
  slot: { pool_guest_units?: number | null; pool_child_weight?: number | null } | null | undefined,
  seed: { guestUnits?: number | null; childWeight?: number | null } | null | undefined,
): { guestUnits: number | null; childWeight: number | null } {
  // A SELECTED SLOT wins as a whole — including when its budget is null. `resources.guest_units`
  // is nullable and NULL means "no unit budget" (0035), so a slot resolving such a pool must read
  // as unconstrained, not fall through to a seed describing a DIFFERENT pool. Falling through
  // would show "this pool holds 2" for a pool that has no cap and send an override the write never
  // needed. The seed only covers the state before any slot exists.
  if (slot) {
    return { guestUnits: budgetUnits(slot.pool_guest_units), childWeight: budgetWeight(slot.pool_child_weight) };
  }
  return { guestUnits: budgetUnits(seed?.guestUnits), childWeight: budgetWeight(seed?.childWeight) };
}


/** The pools to size the picker against: the named category's pools when present,
 *  otherwise a single venue-wide aggregate fallback. */
function poolsFor(config: VenueConfig, poolSlug?: string): PrivatePoolLimits[] {
  const pools = poolSlug ? config.privatePoolLimits?.[poolSlug] : undefined;
  return pools?.length
    ? pools
    : [{
        maxAdults: config.privateMaxAdults,
        maxChildren: config.privateMaxChildren,
        guestUnits: config.privateGuestUnits,
        childWeight: config.privateChildWeight,
        minParty: config.privateMinParty ?? 1,
      }];
}

function units(limits: PrivatePoolLimits): number {
  return limits.guestUnits || limits.maxAdults;
}
function childWeight(limits: PrivatePoolLimits): number {
  return limits.childWeight || 1;
}

/** Max adults ONE pool allows given a chosen number of children. */
function poolAdultsMax(L: PrivatePoolLimits, children: number): number {
  const budget = Math.floor(units(L) - Math.max(0, children) * childWeight(L) + EPS);
  return Math.min(L.maxAdults, budget);
}

/** Max children ONE pool allows given a chosen number of adults. */
function poolChildrenMax(L: PrivatePoolLimits, adults: number): number {
  const budget = Math.floor((units(L) - Math.max(1, adults)) / childWeight(L) + EPS);
  return Math.max(0, Math.min(L.maxChildren, budget));
}

/** Max adults a private CATEGORY allows given a chosen number of children — the
 *  most generous pool: max over pools of min(max_adults, floor(guest_units −
 *  children·child_weight)). */
export function privateAdultsMax(config: VenueConfig, children: number, poolSlug?: string): number {
  return Math.max(1, ...poolsFor(config, poolSlug).map((L) => poolAdultsMax(L, children)));
}

/** Max children a private CATEGORY allows given a chosen number of adults — the
 *  most generous pool: max over pools of min(max_children, floor((guest_units −
 *  adults) / child_weight)). */
export function privateChildrenMax(config: VenueConfig, adults: number, poolSlug?: string): number {
  if (!config.allowChildren) return 0;
  return Math.max(0, ...poolsFor(config, poolSlug).map((L) => poolChildrenMax(L, adults)));
}

/** Mode-aware adult cap. Communal is the live entry cap, optionally clamped by a
 *  selected slot's live seats_left (the staff sheet passes it; the customer flow
 *  gates per-slot separately). Private uses the unit-budget model above, scoped
 *  to the selected category (poolSlug) when given. */
export function adultsMaxFor(
  config: VenueConfig,
  mode: "private" | "communal",
  children: number,
  seatsLeft?: number | null,
  poolSlug?: string,
): number {
  if (mode === "communal") {
    return seatsLeft != null ? Math.min(config.communalMaxAdults, seatsLeft) : config.communalMaxAdults;
  }
  return privateAdultsMax(config, config.allowChildren ? children : 0, poolSlug);
}

/** Mode-aware ADULT floor — the minimum group size a booking must have. Communal
 *  uses The Commons' min; private uses the SMALLEST min across the selected
 *  category's pools (so the most permissive pool stays reachable — availability
 *  then hides times where no fitting pool is free). Mirrors how the picker caps
 *  take the most-generous pool. Defaults to 1 everywhere. The Remarkables hire
 *  is NOT handled here — the flows read its own minParty from config.remarkables. */
export function adultsMinFor(
  config: VenueConfig,
  mode: "private" | "communal",
  poolSlug?: string,
): number {
  if (mode === "communal") return Math.max(1, config.communalMinParty ?? 1);
  return Math.max(1, Math.min(...poolsFor(config, poolSlug).map((L) => L.minParty)));
}

/** Mode-aware child cap (communal has no children), scoped to the selected category. */
export function childrenMaxFor(
  config: VenueConfig,
  mode: "private" | "communal",
  adults: number,
  poolSlug?: string,
): number {
  if (mode === "communal") return 0;
  return privateChildrenMax(config, adults, poolSlug);
}
